import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatDate } from './formatDate';
import { videosApi, tagsApi } from '../../services/api';
import {
  Video, Plus, Search, Edit3, Trash2, MoreVertical,
  X, AlertCircle, ExternalLink, Loader2, Upload, Check, Play,
  Tag, ChevronDown, Send, Archive
} from 'lucide-react';

// Reusable tag dropdown (same pattern as PolicyManagement)
const TAG_COLORS = [
  'bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700','bg-rose-100 text-rose-700','bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700','bg-orange-100 text-orange-700',
];
function tagColor(id, allTags) {
  const idx = allTags.findIndex(t => t.id === id);
  return TAG_COLORS[(idx < 0 ? 0 : idx) % TAG_COLORS.length];
}

function isPublished(video) {
  return video.is_published === true || video.is_published === 1 || video.is_published === '1';
}

function statusClasses(video) {
  return isPublished(video)
    ? 'bg-green-100 text-green-700 border-green-200'
    : 'bg-amber-100 text-amber-700 border-amber-200';
}

function TagDropdown({ allTags, selectedIds, onChange, onCreateTag }) {
  const [open, setOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [tagError, setTagError] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  function toggle(id) {
    onChange(selectedIds.includes(id) ? [] : [id]);
    setOpen(false);
  }
  async function handleCreateTag() {
    const name = newTagName.trim();
    if (!name || creatingTag) return;

    const existing = allTags.find(t => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!selectedIds.includes(existing.id)) {
        onChange([existing.id]);
      }
      setNewTagName('');
      setTagError('');
      return;
    }

    try {
      setCreatingTag(true);
      setTagError('');
      await onCreateTag(name);
      setNewTagName('');
    } catch (err) {
      setTagError(err.message || 'Failed to create resource tab');
    } finally {
      setCreatingTag(false);
    }
  }
  const selected = allTags.filter(t => selectedIds.includes(t.id));
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] transition-all">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {selected.length === 0
            ? <span className="text-gray-400">Select resource tabs…</span>
            : selected.map(t => (
                <span key={t.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tagColor(t.id, allTags)}`}>
                  {t.name}
                  <span role="button" onClick={e => { e.stopPropagation(); toggle(t.id); }} className="hover:opacity-60 cursor-pointer"><X className="w-3 h-3" /></span>
                </span>
              ))
          }
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={e => {
                  setNewTagName(e.target.value);
                  setTagError('');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateTag();
                  }
                }}
                placeholder="Create new resource tab..."
                className="min-w-0 flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF]"
              />
              <button
                type="button"
                onClick={handleCreateTag}
                disabled={!newTagName.trim() || creatingTag}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#00AAFF] hover:bg-[#0088cc] text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingTag ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add
              </button>
            </div>
            {tagError && <p className="mt-1.5 text-xs text-red-500">{tagError}</p>}
          </div>

          <div className="py-1 max-h-44 overflow-y-auto">
          {allTags.length === 0
            ? <p className="px-4 py-3 text-xs text-gray-400">No resource tabs yet. Add one above.</p>
            : allTags.map(tag => {
                const isSel = selectedIds.includes(tag.id);
                return (
                  <button key={tag.id} type="button" onClick={() => toggle(tag.id)}
                    className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tagColor(tag.id, allTags)}`}>{tag.name}</span>
                    {isSel && <Check className="w-4 h-4 text-[#00AAFF]" />}
                  </button>
                );
              })
          }
          </div>
        </div>
      )}
    </div>
  );
}

function VideoManagement({ onResourcesChanged }) {
  const [videos, setVideos] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({ title: '', videoUrl: '', blobName: '', tagIds: [] });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTagId, setActiveTagId] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [deleteNotice, setDeleteNotice] = useState('');
  const deleteNoticeTimer = useRef(null);
  const fileInputRef = useRef(null);
  const actionButtonRefs = useRef({});
  const fetchId = useRef(0);

  const fetchVideos = useCallback(async () => {
    const id = ++fetchId.current;
    try {
      setError(null);
      const [data, tags] = await Promise.all([videosApi.getAll(), tagsApi.getAll()]);
      if (id !== fetchId.current) return;
      if (!Array.isArray(data) || !Array.isArray(tags) ||
          data.some(video => !video.id || typeof video.title !== 'string' || !Array.isArray(video.tags))) {
        throw new Error('Invalid video data from server');
      }
      setVideos(data);
      setAllTags(tags);
    } catch (err) {
      if (id === fetchId.current) setError(err.message);
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
    return () => { fetchId.current += 1; };
  }, [fetchVideos]);

  function refreshPortalResources() {
    if (onResourcesChanged) {
      onResourcesChanged().catch((err) => console.error('Failed to refresh portal resources:', err));
    }
  }

  function openCreateModal() {
    setEditingVideo(null);
    setFormData({ title: '', videoUrl: '', blobName: '', tagIds: [] });
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadedBytes(0);
    setShowModal(true);
  }

  function openEditModal(video) {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      videoUrl: video.video_url || '',
      blobName: video.blob_name || '',
      tagIds: (video.tags || []).slice(0, 1).map(t => t.id)
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadedBytes(0);
    setShowModal(true);
  }

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { alert('File must be under 500MB'); return; }
    setSelectedFile(file);
    setFormData(prev => ({ ...prev, videoUrl: '', blobName: '' }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      let payload = { ...formData, tags: formData.tagIds };
      if (selectedFile) {
        setUploadingFile(true);
        const result = await videosApi.uploadFile(selectedFile, p => {
          setUploadProgress(p);
          // Compute uploaded bytes from percentage × file size
          setUploadedBytes(Math.round((p / 100) * selectedFile.size));
        });
        payload.blobName = result.blobName;
        payload.videoUrl = result.url;
        setUploadingFile(false);
      }
      if (editingVideo) { await videosApi.update(editingVideo.id, payload); }
      else { await videosApi.create(payload); }
      setShowModal(false);
      await fetchVideos();
      refreshPortalResources();
    } catch (err) {
      alert(err.message);
      setUploadingFile(false);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(video) {
    if (isPublished(video)) {
      setActiveMenu(null);
      setDeleteNotice('Unpublish this video before deleting it.');
      clearTimeout(deleteNoticeTimer.current);
      deleteNoticeTimer.current = setTimeout(() => setDeleteNotice(''), 4500);
      return;
    }
    if (!window.confirm(`Delete "${video.title}"?\n\nThis permanently removes the video.`)) return;
    try {
      await videosApi.delete(video.id);
      setActiveMenu(null);
      await fetchVideos();
      refreshPortalResources();
    }
    catch (err) { alert('Failed to delete: ' + err.message); }
  }

  async function handlePublish(id) {
    try {
      setActiveMenu(null);
      await videosApi.publish(id);
      await fetchVideos();
      refreshPortalResources();
    } catch (err) {
      alert('Failed to publish: ' + err.message);
    }
  }

  async function handleUnpublish(video) {
    if (!window.confirm('Are you sure you want to unpublish this video?\n\nThe video will no longer be accessible to users.')) return;
    try {
      await videosApi.unpublish(video.id);
      setActiveMenu(null);
      await fetchVideos();
      refreshPortalResources();
    } catch (err) {
      alert('Failed to unpublish: ' + err.message);
    }
  }

  function openActionMenu(video) {
    if (activeMenu === video.id) {
      setActiveMenu(null);
      return;
    }

    const rect = actionButtonRefs.current[video.id]?.getBoundingClientRect();
    if (rect) {
      setMenuPosition({
        top: rect.bottom + 8,
        left: Math.max(rect.right - 192, 12),
      });
    }
    setActiveMenu(video.id);
  }

  async function handleCreateTag(name) {
    const tag = await tagsApi.create(name);
    setAllTags(await tagsApi.getAll());
    setFormData(prev => ({ ...prev, tagIds: [tag.id] }));
    return tag;
  }

  const filtered = videos.filter(v => {
    const matchesSearch = v.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = !activeTagId || (v.tags || []).some(t => t.id === activeTagId);
    return matchesSearch && matchesTag;
  });
  const menuVideo = videos.find(v => v.id === activeMenu);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-gray-200 rounded w-48" />
        {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load videos</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={fetchVideos} className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-widest">
          Videos
          <span className="ml-2 text-gray-400 font-normal normal-case tracking-normal">
            {filtered.length}{filtered.length !== videos.length ? ` of ${videos.length}` : ' total'}
          </span>
        </span>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-lg px-2.5 h-8">
            <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-xs text-gray-700 placeholder-gray-400 w-36"
            />
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-1.5 h-8 px-3 bg-[#00AAFF] hover:bg-[#0088cc] text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Video
          </button>
        </div>
      </div>

      {/* Tag filter bar */}
      {allTags.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <button onClick={() => setActiveTagId(null)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!activeTagId ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>All</button>
          {allTags.map(tag => (
            <button key={tag.id} onClick={() => setActiveTagId(activeTagId === tag.id ? null : tag.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${activeTagId === tag.id ? 'bg-[#00AAFF] text-white' : `${tagColor(tag.id, allTags)} hover:opacity-80`}`}>
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            {searchQuery ? 'No results found' : 'No videos yet'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Title</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Resource Tabs</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Date</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Preview</th>
                <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(video => (
                <tr key={video.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-purple-50 flex items-center justify-center flex-shrink-0">
                        <Play className="w-3 h-3 text-purple-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate max-w-[220px]">{video.title}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(video.tags || []).length > 0
                        ? video.tags.map(tag => (
                            <button key={tag.id} onClick={() => setActiveTagId(activeTagId === tag.id ? null : tag.id)}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-opacity hover:opacity-70 ${tagColor(tag.id, allTags)}`}>
                              {tag.name}
                            </button>
                          ))
                        : <span className="text-gray-300 text-xs">—</span>
                      }
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusClasses(video)}`}>
                      {isPublished(video) ? <Send className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                      {isPublished(video) ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {formatDate(video.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    {video.video_url || video.blob_name
                      ? <a href={videosApi.viewUrl(video.id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#00AAFF] hover:underline"><ExternalLink className="w-3 h-3" />Preview</a>
                      : <span className="text-gray-300 text-xs">—</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        ref={el => { actionButtonRefs.current[video.id] = el; }}
                        onClick={() => openActionMenu(video)}
                        className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500"
                        title="More actions"
                        aria-label={`Open actions for ${video.title}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {activeMenu && menuVideo && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)} />
          <div
            className="fixed z-50 w-48 bg-white rounded-xl border border-gray-200 shadow-lg py-1 animate-slide-up"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {!isPublished(menuVideo) && (
              <button
                type="button"
                onClick={() => {
                  setActiveMenu(null);
                  openEditModal(menuVideo);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Edit3 className="w-4 h-4 text-gray-400" />
                Edit
              </button>
            )}

            {isPublished(menuVideo) && (
              <button
                type="button"
                onClick={() => handleUnpublish(menuVideo)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors"
              >
                <Archive className="w-4 h-4" />
                Unpublish
              </button>
            )}

            {!isPublished(menuVideo) && (
              <button
                type="button"
                onClick={() => handlePublish(menuVideo.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
              >
                <Send className="w-4 h-4" />
                Publish
              </button>
            )}

            {!isPublished(menuVideo) && <>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                onClick={() => handleDelete(menuVideo)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>}
          </div>
        </>
      )}

      {/* Add / Edit Modal */}
      {deleteNotice && (
        <div className="fixed right-5 bottom-5 z-[60] flex max-w-sm items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 shadow-lg animate-slide-up" role="status">
          <AlertCircle className="h-5 w-5 flex-none" />
          <span>{deleteNotice}</span>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={() => !uploadingFile && !submitting && setShowModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl animate-slide-up overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Video className="w-5 h-5 text-purple-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{editingVideo ? 'Edit Video' : 'Add New Video'}</h2>
              </div>
              {!uploadingFile && !submitting && (
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter video title"
                  required
                  disabled={uploadingFile || submitting}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] transition-all disabled:opacity-60"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resource Tab <span className="font-normal text-gray-400">(control portal placement)</span>
                </label>
                <TagDropdown
                  allTags={allTags}
                  selectedIds={formData.tagIds}
                  onChange={tagIds => setFormData({ ...formData, tagIds })}
                  onCreateTag={handleCreateTag}
                />
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Video File <span className="text-red-500">*</span></label>
                <div className={`border-2 rounded-xl transition-colors ${uploadingFile ? 'border-[#00AAFF] bg-[#00AAFF]/5' : 'border-dashed border-gray-200 hover:border-[#00AAFF]'}`}>
                  {uploadingFile ? (
                    <div className="p-6">
                      {/* File info */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Video className="w-4 h-4 text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{selectedFile?.name}</p>
                          <p className="text-xs text-gray-400">
                            {(uploadedBytes / (1024 * 1024)).toFixed(1)} MB
                            {' / '}
                            {selectedFile ? (selectedFile.size / (1024 * 1024)).toFixed(1) + ' MB' : ''}
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-[#00AAFF] tabular-nums">{uploadProgress}%</span>
                      </div>

                      {/* Progress bar */}
                      <div className="relative w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-[#00AAFF] rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-[#00AAFF] font-medium">
                          {uploadProgress < 80 ? 'Uploading to server…' : uploadProgress < 100 ? 'Saving to Azure…' : 'Done!'}
                        </p>
                        <p className="text-xs text-gray-400 tabular-nums">{uploadProgress}% complete</p>
                      </div>
                    </div>
                  ) : selectedFile || formData.blobName ? (
                    <div className="p-5 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{selectedFile?.name || 'Video uploaded'}</p>
                        {selectedFile && <p className="text-xs text-gray-400">{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB · Ready to upload</p>}
                      </div>
                      <button type="button" onClick={() => { setSelectedFile(null); setFormData(prev => ({ ...prev, videoUrl: '', blobName: '' })); }} className="p-1.5 hover:bg-red-100 rounded-lg text-gray-400 hover:text-red-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block p-8 text-center">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                        <Upload className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">Click to select a video file</p>
                      <p className="text-xs text-gray-400 mt-1">MP4, WebM up to 500MB</p>
                      <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileSelect} className="hidden" />
                    </label>
                  )}
                </div>

              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} disabled={uploadingFile || submitting} className="px-5 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all disabled:opacity-40">Cancel</button>
                <button type="submit" disabled={submitting || uploadingFile || (!selectedFile && !formData.blobName)} className="flex items-center gap-2 px-6 py-2.5 bg-[#00AAFF] hover:bg-[#0088cc] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingVideo ? 'Update Video' : 'Add Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default VideoManagement;
